import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import QuoteClientAgreementNotice from '../components/quotes/QuoteClientAgreementNotice'
import QuoteDocument from '../components/quotes/QuoteDocument'
import SaveQuotePdfButton from '../components/quotes/SaveQuotePdfButton'
import PipelineStepper from '../components/vendor/PipelineStepper'
import VendorDashboardHeader from '../components/vendor/VendorDashboardHeader'
import { VendorInlineLoader } from '../components/vendor/VendorDashboardShell'
import StarterPlanBanner from '../components/vendor/StarterPlanBanner'
import {
  convertQuoteToProject,
  fetchVendorQuote,
  openVendorQuoteContract,
  updateQuote,
  uploadQuoteContract,
} from '../services/quoteService'
import { fetchVendorPlanUsage } from '../services/planService'
import { sendQuoteEmail } from '../services/vendorExtrasService'
import { getApiErrorMessage } from '../utils/apiErrors'
import type { VendorPlanUsage } from '../types/plan'
import type { Quote, QuoteLineItemInput } from '../types/quote'
import { formatUsDate } from '../utils/calendarHelpers'
import { formatQuoteMoney } from '../utils/formatQuoteMoney'
import { getQuotePipelineSteps } from '../utils/quotePipeline'
import {
  applyQuoteDiscount,
  type QuoteDiscountType,
} from '../utils/quoteDiscount'
import toast from 'react-hot-toast'

const statusLabel: Record<Quote['status'], string> = {
  draft: 'Draft',
  sent: 'Awaiting response',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  converted: 'Converted to project',
}

const emptyLineItem = (): QuoteLineItemInput => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
})

const VendorQuoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const quoteId = Number(id)
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const [quote, setQuote] = useState<Quote | null>(null)
  const [quotePath, setQuotePath] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [planUsage, setPlanUsage] = useState<VendorPlanUsage | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [contractUploadTitle, setContractUploadTitle] = useState('Service agreement')
  const [contractUploadFile, setContractUploadFile] = useState<File | null>(null)

  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editLineItems, setEditLineItems] = useState<QuoteLineItemInput[]>([emptyLineItem()])
  const [discountType, setDiscountType] = useState<QuoteDiscountType>('percent')
  const [discountValue, setDiscountValue] = useState('')

  const loadQuote = useCallback(async () => {
    if (!quoteId) return
    try {
      setError('')
      const [data, usage] = await Promise.all([fetchVendorQuote(quoteId), fetchVendorPlanUsage()])
      setQuote(data.quote)
      setQuotePath(data.quotePath)
      setPlanUsage(usage)
    } catch {
      setError('Failed to load quote')
    } finally {
      setLoading(false)
    }
  }, [quoteId])

  useEffect(() => {
    loadQuote()
  }, [loadQuote])

  const canEditQuote = quote?.status === 'draft' || quote?.status === 'sent'

  const startEditing = () => {
    if (!quote) return
    setEditTitle(quote.title)
    setEditNotes(quote.notes ?? '')
    setEditLineItems(
      quote.lineItems.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      }))
    )
    setDiscountValue('')
    setDiscountType('percent')
    setEditing(true)
  }

  const updateEditLineItem = (
    index: number,
    field: keyof QuoteLineItemInput,
    value: string | number
  ) => {
    setEditLineItems((prev) => {
      const next = [...prev]
      const current = next[index]
      if (!current) return prev
      next[index] = { ...current, [field]: value }
      return next
    })
  }

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!quoteId || !editTitle.trim()) return

    let lineItems = editLineItems
      .filter((item) => item.description.trim())
      .map((item) => ({
        description: item.description.trim(),
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
      }))

    if (lineItems.length === 0) {
      setError('Add at least one line item')
      return
    }

    const discountNum = Number.parseFloat(discountValue)
    if (discountValue.trim() !== '' && Number.isFinite(discountNum) && discountNum > 0) {
      lineItems = applyQuoteDiscount(lineItems, discountType, discountNum)
    }

    setSubmitting(true)
    setError('')
    try {
      const result = await updateQuote(quoteId, {
        title: editTitle.trim(),
        notes: editNotes,
        lineItems,
      })
      setQuote(result.quote)
      setQuotePath(result.quotePath)
      setEditing(false)
      toast.success('Quote updated')
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update quote'))
    } finally {
      setSubmitting(false)
    }
  }

  const getQuoteFullUrl = (path: string) => `${window.location.origin}${path}`

  const copyQuoteLink = () => {
    if (!quotePath) return
    navigator.clipboard.writeText(getQuoteFullUrl(quotePath))
  }

  const openQuoteEmailDraft = () => {
    if (!quote || !quotePath) return
    const fullUrl = getQuoteFullUrl(quotePath)
    const subject = encodeURIComponent(`Your quote — ${quote.title}`)
    const body = encodeURIComponent(
      `Hi${quote.clientName ? ` ${quote.clientName}` : ''},\n\n` +
        `Here's your quote from us. Open the link below to review and accept or decline — no account needed:\n\n` +
        `${fullUrl}\n\n` +
        (quote.contract
          ? `We've included our contract with this quote — you can review it from the same link before accepting.\n\n`
          : '') +
        `Total: ${formatQuoteMoney(quote.totalAmount, quote.currency)}\n\n` +
        `Let us know if you have any questions!\n`
    )
    window.location.href = `mailto:${quote.clientEmail}?subject=${subject}&body=${body}`
  }

  const handleSendQuoteEmail = async () => {
    if (!quoteId) return
    setSubmitting(true)
    try {
      const result = await sendQuoteEmail(quoteId)
      if (result.sent) {
        toast.success('Quote email sent')
      } else if (result.skippedReason === 'EMAIL_NOT_CONFIGURED') {
        toast.error('Email is not configured on the server — use “Open in email app” instead')
      } else {
        toast.error('Could not send quote email')
      }
    } catch {
      toast.error('Could not send quote email')
    } finally {
      setSubmitting(false)
    }
  }

  const handleConvert = async () => {
    if (!quoteId) return
    setSubmitting(true)
    setError('')
    try {
      const result = await convertQuoteToProject(quoteId)
      navigate(`/dashboard/projects/${result.projectId}`)
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to convert quote'))
    } finally {
      setSubmitting(false)
    }
  }

  const canManageContract =
    quote?.status !== 'converted' && quote?.status !== 'declined' && !quote?.contract?.acknowledgedAt

  const handleUploadContract = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!quoteId || !contractUploadFile) return

    setSubmitting(true)
    setError('')
    try {
      const result = await uploadQuoteContract(quoteId, {
        contractTitle: contractUploadTitle.trim() || quote?.contract?.title || 'Service agreement',
        contractFile: contractUploadFile,
      })
      setQuote(result.quote)
      setQuotePath(result.quotePath)
      setContractUploadFile(null)
      toast.success('Contract uploaded')
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined
      setError(message || 'Failed to upload contract')
    } finally {
      setSubmitting(false)
    }
  }

  const atProjectLimit = planUsage?.limits.activeProjects.atLimit === true

  if (loading) {
    return <VendorInlineLoader />
  }

  if (!quote) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="max-w-md w-full vendor-card p-6 text-center">
          <h2 className="text-lg font-medium text-slate-900">Quote not found</h2>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <Link to="/dashboard/quotes" className="mt-4 inline-block vendor-link">
            Back to quotes
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <VendorDashboardHeader
        active="quotes"
        userEmail={user?.email}
        onLogout={() => logout()}
      />

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="no-print flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <Link to="/dashboard/quotes" className="text-sm vendor-link">
              ← All quotes
            </Link>
            <h1 className="text-xl font-semibold text-slate-900 mt-1">{quote.title}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEditQuote && !editing && (
              <button type="button" onClick={startEditing} className="vendor-btn-outline">
                Edit quote
              </button>
            )}
            <SaveQuotePdfButton quoteTitle={quote.title} />
          </div>
        </div>
        <StarterPlanBanner usage={planUsage} focus="projects" />

        {error && (
          <div className="no-print rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}

        <div className="no-print">
          <QuoteClientAgreementNotice variant="vendor" />
        </div>

        <div className="no-print">
          <PipelineStepper steps={getQuotePipelineSteps(quote)} title="Quote progress" />
        </div>

        {editing ? (
          <form onSubmit={handleSaveEdit} className="no-print vendor-card p-6 space-y-4">
            <h2 className="font-medium text-slate-900">Edit quote</h2>
            <p className="text-sm text-slate-600">
              Set your prices here. Client budget on an inquiry is reference only — it does not lock
              this total.
            </p>
            <input
              required
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Quote title"
            />
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Notes for your client (optional)"
            />

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
              <p className="text-xs font-medium text-slate-700">Apply discount (optional)</p>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="text-xs text-slate-600">
                  Type
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as QuoteDiscountType)}
                    className="mt-1 block rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat ($)</option>
                  </select>
                </label>
                <label className="text-xs text-slate-600">
                  Amount
                  <input
                    type="number"
                    min={0}
                    step={discountType === 'percent' ? 1 : 0.01}
                    placeholder={discountType === 'percent' ? '10' : '100'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="mt-1 block w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </label>
              </div>
              <p className="text-xs text-slate-500">
                Adds or replaces a Discount line when you save. Leave blank to keep line items as
                shown.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-900">Line items</h3>
                <button
                  type="button"
                  onClick={() => setEditLineItems((prev) => [...prev, emptyLineItem()])}
                  className="text-sm vendor-link"
                >
                  + Add item
                </button>
              </div>
              {editLineItems.map((item, index) => (
                <div
                  key={index}
                  className="grid gap-2 sm:grid-cols-12 items-end border-b border-gray-100 pb-3"
                >
                  <div className="sm:col-span-6">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      value={item.description}
                      onChange={(e) => updateEditLineItem(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Qty</label>
                    <input
                      type="number"
                      min={0.01}
                      step={0.01}
                      value={item.quantity}
                      onChange={(e) =>
                        updateEditLineItem(index, 'quantity', parseFloat(e.target.value) || 0)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Unit price
                    </label>
                    <input
                      type="number"
                      step={0.01}
                      value={item.unitPrice}
                      onChange={(e) =>
                        updateEditLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  {editLineItems.length > 1 && (
                    <div className="sm:col-span-1">
                      <button
                        type="button"
                        onClick={() =>
                          setEditLineItems((prev) => prev.filter((_, i) => i !== index))
                        }
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="vendor-btn-primary">
                {submitting ? 'Saving...' : 'Save changes'}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => setEditing(false)}
                className="vendor-btn-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <QuoteDocument
            quote={{
              title: quote.title,
              clientName: quote.clientName,
              clientEmail: quote.clientEmail,
              eventDate: quote.eventDate,
              location: quote.location,
              notes: quote.notes,
              currency: quote.currency,
              totalAmount: quote.totalAmount,
              lineItems: quote.lineItems,
              expiresAt: quote.expiresAt,
              statusLabel: statusLabel[quote.status],
            }}
          />
        )}

        {quote.contract && (
          <section className="no-print vendor-card p-6 space-y-3">
            <h2 className="font-medium text-gray-900">Attached contract</h2>
            <p className="text-sm text-gray-600">
              {quote.contract.title} · {quote.contract.fileName}
            </p>
            {!quote.contract.fileAvailable && (
              <p className="text-sm text-red-800 bg-red-50 rounded-lg px-3 py-2">
                The contract PDF is missing on the server (often after a redeploy). Re-upload the
                file below so your client can review it on the quote link.
              </p>
            )}
            {quote.contract.acknowledgedAt ? (
              <p className="text-sm text-green-800">
                Signed by {quote.contract.acknowledgementLegalName ?? 'client'} on{' '}
                {formatUsDate(quote.contract.acknowledgedAt)}
                . Client still needs to pay deposit to become a booked client.
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                View-only for your client until they accept the quote, then signing unlocks on the
                quote link.
              </p>
            )}
            {quote.contract.fileAvailable && (
              <button
                type="button"
                onClick={() => openVendorQuoteContract(quote.id)}
                className="vendor-btn-outline"
              >
                View contract PDF
              </button>
            )}
          </section>
        )}

        {canManageContract && (
          <section className="no-print bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="font-medium text-gray-900">
              {quote.contract ? 'Re-upload contract PDF' : 'Attach contract PDF'}
            </h2>
            <p className="text-sm text-gray-600">
              {quote.contract
                ? 'Replace the contract file if it failed to upload or was lost after a server restart.'
                : 'Add a contract PDF for this quote so clients can review it on the quote link.'}
            </p>
            <form onSubmit={handleUploadContract} className="space-y-3 max-w-md">
              <div>
                <label htmlFor="quote-contract-reupload-title" className="block text-xs font-medium text-gray-700 mb-1">
                  Contract title
                </label>
                <input
                  id="quote-contract-reupload-title"
                  value={contractUploadTitle}
                  onChange={(e) => setContractUploadTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label htmlFor="quote-contract-reupload-file" className="block text-xs font-medium text-gray-700 mb-1">
                  Contract PDF
                </label>
                <input
                  id="quote-contract-reupload-file"
                  type="file"
                  accept="application/pdf,.pdf"
                  required
                  onChange={(e) => setContractUploadFile(e.target.files?.[0] ?? null)}
                  className="w-full text-sm text-gray-700"
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !contractUploadFile}
                className="vendor-btn-primary"
              >
                {submitting ? 'Uploading...' : quote.contract ? 'Replace contract' : 'Upload contract'}
              </button>
            </form>
          </section>
        )}

        {quote.status !== 'converted' && (
          <section className="no-print bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="font-medium text-gray-900">Client link</h2>
            <p className="text-sm text-gray-600">
              Send this link by email or text. Your client can review, save a PDF, and respond without an account.
            </p>
            <code className="block break-all text-xs bg-gray-50 p-3 rounded border text-gray-800">
              {getQuoteFullUrl(quotePath)}
            </code>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={copyQuoteLink}
                className="vendor-btn-outline"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={() => void handleSendQuoteEmail()}
                disabled={submitting}
                className="vendor-btn-primary"
              >
                Send email
              </button>
              <button
                type="button"
                onClick={openQuoteEmailDraft}
                className="vendor-btn-outline"
              >
                Open in email app
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Expires {formatUsDate(quote.expiresAt)}
            </p>
          </section>
        )}

        {quote.status === 'accepted' && !quote.projectId && (
          <section className="no-print bg-green-50 rounded-lg border border-green-200 p-6 space-y-3">
            <h2 className="font-medium text-green-900">Client accepted the quote — next steps</h2>
            <p className="text-sm text-green-800">
              Accepting the quote is only one part of the agreement. Convert to a project to collect
              contract signature and deposit, then send your client portal invite.
            </p>
            <button
              type="button"
              onClick={handleConvert}
              disabled={submitting || atProjectLimit}
              className="px-4 py-2 text-sm text-white bg-green-700 rounded-md hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Creating project...' : 'Convert to project'}
            </button>
            {atProjectLimit && (
              <p className="text-sm text-green-900">
                Starter plan allows 1 active project. Complete or cancel your current project, or upgrade to Pro.
              </p>
            )}
          </section>
        )}

        {quote.status === 'converted' && quote.projectId && (
          <section className="no-print bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">
              This quote was converted to a project.{' '}
              <Link
                to={`/dashboard/projects/${quote.projectId}`}
                className="vendor-link font-medium"
              >
                Open project →
              </Link>
            </p>
          </section>
        )}
      </main>
    </div>
  )
}

export default VendorQuoteDetail
